import React from 'react'
import cn from 'classnames'
import Section from '../landings/about/data/index'

import styles from '../../components/landings/about/about.module.scss'

const Paragraphs = [
  {
    title: 'What is puppy financing?',
    text: (
      <p className={styles.text}>
        Puppy financing, also called Buy Now Pay Later or Pay In 4, is a
        flexible payment option that allows you to split the cost of purchasing
        your puppy over time. Rather than paying the full puppy cost upfront,
        you make monthly payments to the lender, which can be a more affordable
        option. Some pay later options are interest-free, making them a
        preferred choice by prospective pet owners.
      </p>
    ),
  },
  {
    title: 'What are the terms for puppy payment plans?',
    text: (
      <p className={styles.text}>
        The payment plans from our partners are designed to be flexible and are
        based on your financial history. Key terms include the down payment,
        duration of the plan, monthly payments, and interest rate. A given
        partner may offer you multiple payment terms for your selection.
      </p>
    ),
  },
  {
    title: 'How do I apply for puppy financing?',
    text: (
      <p className={styles.text}>
        Applying for puppy financing is simple. Just select the financing option
        that suits you and follow the steps to apply directly through our
        website or with our financing partners. You’ll receive an instant
        response for some options, while others may reply back up to 24 hours
        later.
      </p>
    ),
  },
  {
    title: 'Can financing be used for a specific breed of puppy?',
    text: (
      <p className={styles.text}>
        Yes, puppy financing is agnostic to your preferred breed. Whether a{' '}
        <a
          className={styles.link}
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/goldendoodle`}
        >
          Goldendoodle
        </a>
        ,{' '}
        <a
          className={styles.link}
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/yorkshire-terrier`}
        >
          Yorkie
        </a>
        , or{' '}
        <a
          className={styles.link}
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale/french-bulldog`}
        >
          French Bulldog
        </a>
        , you can apply for and be approved for puppy financing.
      </p>
    ),
  },
  {
    title: 'What happens once I am approved to finance a puppy?',
    text: (
      <p className={styles.text}>
        Once you're approved, you can use the funds from the lender to purchase
        your puppy at Mawoo Pets. Our team will coordinate the payment
        allocation for you, whereby the lender pays us directly for your puppy.
        You will then repay the lender through fixed monthly installments.
      </p>
    ),
  },
  {
    title: 'Do you offer puppy financing for low or bad credit?',
    text: (
      <p className={styles.text}>
        Yes, there are financing options available even if you have bad credit
        or no credit. Interest rates for these puppy financing options are
        typically higher. That said, some Buy Now, Pay Later puppy financing
        options, like Pay In 4 programs, don’t require a credit check, and
        simply split your cost over 4 payments that are interest-free.
      </p>
    ),
  },
  {
    title: 'Can I get a puppy with no credit check?',
    text: (
      <p className={styles.text}>
        Yes. All the financing options we offer only perform a soft credit
        check, which doesn’t impact your credit score. Instead of checking your
        credit history (as a hard credit check would do), our financing partners
        look at other factors like your income and employment status. So you can
        check what you qualify for without worrying about negatively impacting
        your credit report.
      </p>
    ),
  },
  {
    title: 'How does puppy financing affect my credit score?',
    text: (
      <p className={styles.text}>
        If you make your payments on-time and consistently, financing a puppy
        can help with establishing credit history and may positively impact your
        credit score. But if you fail to make payments or take on too much debt
        compared to your eligible amount, you may negatively impact your score.
      </p>
    ),
  },
  {
    title: 'What is the payment duration of your loans?',
    text: (
      <p className={styles.text}>
        Popular pet financing options range from Pay In 4 options to longer-term
        Buy Now Pay Later (BNPL) programs. Pay in 4 options span 6 to 8 weeks
        for repayment. Longer-term loans can vary anywhere from 6 to 60 months.
        The longer the loan, the higher your interest rate tends to be.
      </p>
    ),
  },
  {
    title: 'What are the interest rates for pet loans?',
    text: (
      <p className={styles.text}>
        Interest rates for pet loans vary depending on the lender and your
        credit score. Options start from 0% APR (e.g., Pay In 4 options) while
        other loans can exceed credit card rates. Before you commit to an
        option, consult our team — we can help you understand the full cost of
        your loan, including interest rates and fees, and to make sure it's the
        right fit for you.
      </p>
    ),
  },
  {
    title: 'Do you offer interest-free financing options? ',
    text: (
      <p className={styles.text}>
        Yes, we offer 0% APR options with our financing partners. This allows
        you to finance your puppy without additional interest costs. You can see
        if you qualify for this option once you apply.
      </p>
    ),
  },
  {
    title: 'Can I pay off my puppy financing early?',
    text: (
      <p className={styles.text}>
        Yes, you can pay off your puppy financing early. Specific lenders may
        charge a prepayment penalty, which you should review before signing the
        agreement.
      </p>
    ),
  },
  {
    title: 'What documentation do I need to apply for financing?',
    text: (
      <p className={styles.text}>
        Typically, you'll need to provide identification and proof of income. If
        more is required, the specific financing provider will reach out to you
        for additional documentation.
      </p>
    ),
  },
  {
    title: 'Are there additional fees with puppy financing?',
    text: (
      <p className={styles.text}>
        Our financing partners outline any additional fees, such as late payment
        fees, early prepayment penalties, or processing fees in your agreement.
        You can consult the lender about the specifics of any fees before
        committing to a financing option.
      </p>
    ),
  },
  {
    title: 'Are there risks involved with puppy financing?',
    text: (
      <p className={styles.text}>
        As with any financing, there are some key risks to consider: 1. You need
        to make sure that you can cover your monthly payments and 2. That the
        interest rate is sensible for you. For a complete breakdown of the pros
        and cons of puppy financing, including risks involved, review our{' '}
        <a
          className={styles.link}
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/blog/pet-financing-a-complete-guide`}
        >
          complete guide to puppy financing.
        </a>
      </p>
    ),
  },
  {
    title: 'What happens if I’m unable to continue payments?',
    text: (
      <p className={styles.text}>
        If you're facing difficulties with your payments, we recommend
        contacting the financing company immediately to discuss possible
        solutions.
      </p>
    ),
  },
]

export default function FAQsAboutFinancing() {
  return (
    <div className={cn([styles.article, styles.faqs])}>
      {Paragraphs?.map((elem, index) => (
        <Section key={index} data={elem} isFirst={index === 0} />
      ))}
    </div>
  )
}
